"use client"

import { useState } from "react"
import { toast } from "sonner"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Paperclip, Send, Save, Loader2 } from "lucide-react"

const departments = [
  "Compliance", "Customer Support", "HR", "Fraud Risk",
  "IT Operations", "Finance", "Risk Management", "Legal",
  "Operations", "Technology", "Internal Audit",
]

const frequencies = ["Ad hoc", "Daily", "Weekly", "Monthly", "Quarterly"]
const urgencyLevels = ["Low", "Medium", "High", "Critical"]

type FormState = {
  department: string
  businessOwner: string
  processName: string
  businessProblem: string
  currentWorkflow: string
  frequency: string
  estimatedTimeSpent: string
  monthlyVolume: string
  systemsInvolved: string
  dataInvolved: string
  expectedBenefit: string
  urgency: string
}

const initialState: FormState = {
  department: "",
  businessOwner: "",
  processName: "",
  businessProblem: "",
  currentWorkflow: "",
  frequency: "",
  estimatedTimeSpent: "",
  monthlyVolume: "",
  systemsInvolved: "",
  dataInvolved: "",
  expectedBenefit: "",
  urgency: "",
}

export function PainPointForm() {
  const [form, setForm] = useState<FormState>(initialState)
  const [submitting, setSubmitting] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)

  function setField(field: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.department || !form.businessOwner || !form.processName || !form.businessProblem) {
      toast.error("Please fill in all required fields.")
      return
    }
    setSubmitting(true)
    try {
      const res = await fetch("http://95.217.189.103:5000/api/pain-points", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          monthlyVolume: Number(form.monthlyVolume) || 0,
          systemsInvolved: form.systemsInvolved.split(",").map((s) => s.trim()).filter(Boolean),
        }),
      })
      const data = await res.json()
      if (data.success) {
        toast.success(`Pain point submitted! ID: ${data.painPointId}`)
        setForm(initialState)
      } else {
        toast.error("Submission failed. Please try again.")
      }
    } catch {
      toast.error("Network error. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  async function handleSaveDraft() {
    setSavingDraft(true)
    await new Promise((r) => setTimeout(r, 600))
    toast.success("Draft saved successfully.")
    setSavingDraft(false)
  }

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-foreground">Submit a Pain Point</h2>
        <p className="text-sm text-muted-foreground">
          Describe a business process challenge. The AI team will review and map it to a candidate AI use case.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* Section 1 */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Department & Ownership</CardTitle>
            <CardDescription className="text-xs">Identify the submitting department and responsible owner.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">
                  Department <span className="text-destructive">*</span>
                </label>
                <Select value={form.department} onValueChange={(v) => setField("department", v)}>
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">
                  Business Owner <span className="text-destructive">*</span>
                </label>
                <Input
                  placeholder="e.g. Head of Compliance Operations"
                  value={form.businessOwner}
                  onChange={(e) => setField("businessOwner", e.target.value)}
                  className="text-sm"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">
                Process Name <span className="text-destructive">*</span>
              </label>
              <Input
                placeholder="e.g. Regulatory Circular Review"
                value={form.processName}
                onChange={(e) => setField("processName", e.target.value)}
                className="text-sm"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 2 */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Problem Description</CardTitle>
            <CardDescription className="text-xs">Describe the business problem and current way of working.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">
                Business Problem / Pain Point <span className="text-destructive">*</span>
              </label>
              <Textarea
                placeholder="Describe the pain point in detail. What is the problem? What impact does it have?"
                rows={3}
                value={form.businessProblem}
                onChange={(e) => setField("businessProblem", e.target.value)}
                className="text-sm resize-none"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Current Workflow Description</label>
              <Textarea
                placeholder="How is this process currently performed? Step by step."
                rows={3}
                value={form.currentWorkflow}
                onChange={(e) => setField("currentWorkflow", e.target.value)}
                className="text-sm resize-none"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Expected Benefit</label>
              <Textarea
                placeholder="What outcomes do you expect from an AI solution?"
                rows={2}
                value={form.expectedBenefit}
                onChange={(e) => setField("expectedBenefit", e.target.value)}
                className="text-sm resize-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 3 */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Volume & Frequency</CardTitle>
            <CardDescription className="text-xs">Help quantify the scale of the problem.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">Frequency</label>
                <Select value={form.frequency} onValueChange={(v) => setField("frequency", v)}>
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {frequencies.map((f) => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">Estimated Time Spent Per Cycle</label>
                <Input
                  placeholder="e.g. 3 hours per circular"
                  value={form.estimatedTimeSpent}
                  onChange={(e) => setField("estimatedTimeSpent", e.target.value)}
                  className="text-sm"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">Monthly Volume</label>
                <Input
                  type="number"
                  placeholder="e.g. 25"
                  value={form.monthlyVolume}
                  onChange={(e) => setField("monthlyVolume", e.target.value)}
                  className="text-sm"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Urgency</label>
              <div className="flex gap-2 flex-wrap">
                {urgencyLevels.map((level) => {
                  const colors: Record<string, string> = {
                    Low: "border-gray-300 text-gray-600",
                    Medium: "border-amber-300 text-amber-700",
                    High: "border-orange-300 text-orange-700",
                    Critical: "border-red-300 text-red-700",
                  }
                  const selected = form.urgency === level
                  return (
                    <button
                      type="button"
                      key={level}
                      onClick={() => setField("urgency", level)}
                      className={`px-3 py-1 rounded-md text-xs font-medium border transition-colors
                        ${selected
                          ? "bg-primary text-primary-foreground border-primary"
                          : `bg-background ${colors[level]} hover:bg-muted`
                        }`}
                    >
                      {level}
                    </button>
                  )
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 4 */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Systems & Data</CardTitle>
            <CardDescription className="text-xs">Identify the systems and data involved in the process.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Systems Involved</label>
              <Input
                placeholder="e.g. Email, Document Repository, CRM (comma-separated)"
                value={form.systemsInvolved}
                onChange={(e) => setField("systemsInvolved", e.target.value)}
                className="text-sm"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Data Involved</label>
              <Textarea
                placeholder="Describe the data used or produced in this process. Note if it contains PII or sensitive data."
                rows={2}
                value={form.dataInvolved}
                onChange={(e) => setField("dataInvolved", e.target.value)}
                className="text-sm resize-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* Attachments */}
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-3 text-muted-foreground border-2 border-dashed border-border rounded-lg p-5 justify-center cursor-pointer hover:border-primary/50 transition-colors">
              <Paperclip className="size-4" />
              <span className="text-sm">Attach supporting documents (PDF, Word, Excel)</span>
              <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
            </div>
          </CardContent>
        </Card>

        <Separator />

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleSaveDraft}
            disabled={savingDraft || submitting}
          >
            {savingDraft ? (
              <><Loader2 className="size-4 mr-2 animate-spin" />Saving...</>
            ) : (
              <><Save className="size-4 mr-2" />Save Draft</>
            )}
          </Button>
          <Button type="submit" disabled={submitting || savingDraft}>
            {submitting ? (
              <><Loader2 className="size-4 mr-2 animate-spin" />Submitting...</>
            ) : (
              <><Send className="size-4 mr-2" />Submit Pain Point</>
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}
